<template>
    <div ref="root" :class="[classes.searchResults.root, { [classes.searchResults.rootLoading]: isLoading }]">
        <component :is="componentFor('SearchForm', components)" v-if="!hideForm" :query="query" @submit="submit" />
        <div ref="statusElement" :class="classes.searchResults.status" aria-live="polite" tabindex="-1">
            <!-- announced for screen readers; the skeleton teasers below show the state visually -->
            <span v-if="isLoading" :class="classes.searchResults.loading">{{ translate('agent-search.results.loading') }}</span>
            <div v-else-if="rateLimited" :class="classes.searchResults.rateLimited">{{ translate('agent-search.results.rate-limited') }}</div>
            <div v-else-if="status === 'error'" :class="classes.searchResults.error">{{ translate('agent-search.results.error') }}</div>
            <div v-else-if="noResults" :class="classes.searchResults.empty">{{ translate('agent-search.results.none') }}</div>
            <span v-else-if="status === 'success' && total > 0" :class="classes.searchResults.count">{{ countText.before }}<span :class="classes.searchResults.countNumber">{{ countText.number }}</span>{{ countText.after }}</span>
        </div>
        <div ref="resultsArea" :class="classes.searchResults.area" :aria-busy="isLoading ? 'true' : 'false'">
            <!-- while loading, skeleton teasers replace the results; they hold nothing focusable -->
            <ol v-if="isLoading" :class="classes.searchResults.skeletonList" aria-hidden="true">
                <li v-for="index in skeletonCount" :key="index" :class="classes.searchResults.skeletonItem">
                    <component :is="componentFor('TeaserSkeleton', components)" />
                </li>
            </ol>
            <div v-else-if="response && status !== 'error'" :class="classes.searchResults.results">
                <ResultBlock
                    v-for="block in response.blocks"
                    :key="block.type"
                    :block="block"
                    :query="query"
                    :show-title="response.blocks.length > 1"
                    :current-search="locationSearch"
                    :exact-total="exactTotal"
                    :teasers="teasers"
                    :components="components"
                    @select="onSelect(block.type, $event)"
                    @page="goToPage"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, toRef, watch } from 'vue'
import { createClient } from '../core/client'
import { translate as translateText, type TranslationKey, type TranslationVariables } from '../core/texts'
import { FALLBACK_LANG } from './composables/useTranslate'
import { CLASSES } from './composables/useClasses'
import { mergeClasses, type ClassOverrides } from '../core/classes'
import type { Item, ResultClickEventPayload, ResultsEventPayload } from '../core/types'
import ResultBlock from './ResultBlock.vue'
import { componentFor, type ComponentMap } from './registry'
import { useSearch } from './composables/useSearch'
import type { TeaserMap } from './teasers/registry'

const props = withDefaults(
    defineProps<{
        /** Base path of the API, e.g. `/api/agent-search`. */
        endpoint: string
        /** Id of the search document. */
        context: string | number
        /** Language of the built-in fallback texts; the texts themselves come from `_translations`. */
        lang?: string
        /** Hides the built-in search form. */
        hideForm?: boolean
        /** Teaser components by teaser key, in addition to the global registry. */
        teasers?: TeaserMap
        /** Components replacing built-in ones for this instance, before the global registry. */
        components?: ComponentMap
        /** CSS classes replacing the defaults (default-classes.json), per component; the web component takes JSON. */
        classes?: ClassOverrides | string
    }>(),
    { lang: 'en', hideForm: false, teasers: undefined, components: undefined, classes: undefined },
)

const emit = defineEmits<{
    'agent-search-results': [payload: ResultsEventPayload]
    'agent-search-result-click': [payload: ResultClickEventPayload]
}>()

const root = ref<HTMLElement | null>(null)
const statusElement = ref<HTMLElement | null>(null)
const resultsArea = ref<HTMLElement | null>(null)

const configured = Boolean(props.endpoint) && props.context !== '' && props.context !== undefined && props.context !== null
const client = configured ? createClient({ endpoint: props.endpoint, context: props.context }) : null

if (!configured) {
    console.warn('[agent-search] endpoint and context are required')
}

const { status, query, page, response, error, locationSearch, exactTotal, submit, goToPage } = useSearch({
    client,
    onResults: (payload) => emit('agent-search-results', payload),
    onPageChange: focusResults,
})

// child components read the fallback language through useTranslate()
provide(FALLBACK_LANG, toRef(props, 'lang'))

const classes = computed(() => mergeClasses(props.classes))
provide(CLASSES, classes)

function translate(key: TranslationKey, variables?: TranslationVariables): string {
    return translateText(key, props.lang, variables)
}
const isLoading = computed(() => status.value === 'loading')

/** As many skeleton teasers as results were shown before (the response is kept while loading), 3 on the first search. */
const DEFAULT_SKELETONS = 3
const skeletonCount = computed(() => {
    const shown = Math.max(0, ...(response.value?.blocks ?? []).map((block) => block.items.length))

    return Math.min(shown || DEFAULT_SKELETONS, 10)
})

// focus inside the results is lost when the skeleton replaces them; it returns to the status afterwards
let focusInResults = false

watch(isLoading, (loading) => {
    if (loading) {
        focusInResults = resultsArea.value?.contains(document.activeElement) ?? false

        return
    }

    if (focusInResults) {
        focusInResults = false
        statusElement.value?.focus({ preventScroll: true })
    }
}, { flush: 'sync' })
const total = computed(() =>
    (response.value?.blocks ?? []).reduce((sum, block) => sum + (block.success ? block.total : 0), 0),
)
// a failed block shows its own error; "no results" would contradict it
const noResults = computed(
    () => status.value === 'success' && (response.value?.blocks ?? []).every((block) => block.success && block.items.length === 0),
)
/** Stands in for `{{total}}`, so the number can be rendered in its own element. */
const COUNT_MARKER = '\u0000'
// exact on the last page, otherwise Google's estimate ("about 120 results"); the text around `{{total}}` comes from the translation
const countText = computed(() => {
    const key = !exactTotal.value ? 'agent-search.results.count-approx' : total.value === 1 ? 'agent-search.results.count-one' : 'agent-search.results.count'
    const [before = '', ...rest] = translate(key, { total: COUNT_MARKER }).split(COUNT_MARKER)
    const number = formatNumber(total.value)

    // a translation without the placeholder still shows the number, before its text ("23 Ergebnisse")
    if (rest.length === 0) {
        return { before: '', number, after: before ? ` ${before}` : '' }
    }

    return { before, number, after: rest.join(number) }
})
// the API answered 429, or the cooldown after it blocked the request: a hint, no automatic retry
const rateLimited = computed(() => status.value === 'error' && error.value?.code === 'RATE_LIMITED')

/** The number with the digit grouping of `lang` (`10.000` in German). */
function formatNumber(value: number): string {
    try {
        return new Intl.NumberFormat(props.lang).format(value)
    } catch {
        // an invalid language tag
        return String(value)
    }
}

function onSelect(blockType: string, payload: { item: Item; position: number }): void {
    emit('agent-search-result-click', {
        item: payload.item,
        position: payload.position,
        page: page.value,
        query: query.value,
        blockType,
    })
}

function focusResults(): void {
    root.value?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    statusElement.value?.focus({ preventScroll: true })
}
</script>
